import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SwitchIcon } from '@sorare/core/src/atoms/icons/SwitchIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Pagination } from '@sorare/core/src/atoms/navigation/Pagination';
import { ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';

import { TrckGroupAsideGWSelector } from 'components/trckGroup/TrckGroupAsideGWSelector';

import type {
  PaginatedRankingsQuery,
  PaginatedRankingsQueryVariables,
  PaginatedRankings_so5Ranking,
} from './__generated__/index.graphql';
import { RankRow } from '../CumulativeRankRewards/CumulativeRankRow';

const PAGINATED_RANKINGS_QUERY = gql`
  query PaginatedRankingsQuery(
    $slug: String!
    $page: Int
    $pageSize: Int!
    $onlyFollowed: Boolean
    $canBeatSo5LineupId: ID
  ) {
    so5 {
      so5Leaderboard(slug: $slug) {
        slug
        so5LeaderboardType
        so5Fixture {
          slug
          ...TrckGroupAsideGWSelector_so5Fixture
        }
        so5RankingsPaginated(
          page: $page
          pageSize: $pageSize
          onlyFollowed: $onlyFollowed
          canBeatSo5LineupId: $canBeatSo5LineupId
        ) {
          currentPage
          totalCount
          pages
          so5Rankings {
            id
            ...RankRow_userRankingInterface
          }
        }
      }
    }
  }
  ${RankRow.fragments.userRankingInterface}
  ${TrckGroupAsideGWSelector.fragments.so5Fixture}
` as TypedDocumentNode<PaginatedRankingsQuery, PaginatedRankingsQueryVariables>;

const Root = styled(Vertical)`
  padding-bottom: var(--double-unit);
`;

const PAGE_SIZE = 20;

type Props = {
  slug: string;
  so5Ranking?: PaginatedRankings_so5Ranking;
  showCanBeatMe: boolean;
};
export const PaginatedRankings = ({
  slug,
  so5Ranking,
  showCanBeatMe,
}: Props) => {
  const track = useEvents();
  const navigate = useNavigate();
  const { generateSportContextPath } = useSportContext();
  const [page, setPage] = useState<number>();
  const [onlyFollowed, setOnlyFollowed] = useState(false);
  const [canBeatMe, setCanBeatMe] = useState(false);
  const [query, { data, loading, called }] = useLazyQuery(
    PAGINATED_RANKINGS_QUERY
  );
  const initialPage = so5Ranking?.ranking
    ? Math.floor((so5Ranking.ranking - 1) / PAGE_SIZE)
    : 0;

  if (!called) {
    query({
      variables: {
        slug,
        pageSize: PAGE_SIZE,
        page: initialPage,
        onlyFollowed,
      },
    });
  }

  const { so5RankingsPaginated, so5Fixture, so5LeaderboardType } =
    data?.so5.so5Leaderboard || {};
  const { so5Rankings, currentPage, pages } = so5RankingsPaginated || {};

  return (
    <Root>
      <SBHorizontal>
        {!!so5Fixture && (
          <TrckGroupAsideGWSelector
            aasmStates={['started', 'closed']}
            selectedFixture={so5Fixture}
            so5LeaderboardType={so5LeaderboardType}
            navigateTo={props => {
              navigate(
                generateSportContextPath(
                  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS,
                  {
                    params: {
                      eventType: so5Fixture.type,
                      leaderboard: props.leaderboardSlug,
                    },
                  }
                )
              );
            }}
          />
        )}
        <Horizontal>
          <Button
            color="tertiary"
            size="small"
            onClick={() => {
              const newOnlyFollowed = !onlyFollowed;
              const newPage = newOnlyFollowed ? 0 : initialPage;
              setPage(newPage);
              setOnlyFollowed(newOnlyFollowed);
              query({
                variables: {
                  slug,
                  pageSize: PAGE_SIZE,
                  page: newPage,
                  onlyFollowed: newOnlyFollowed,
                },
              });
            }}
          >
            <FormattedMessage
              id="leaderboard.filters.friendsOnly"
              defaultMessage="Friends only"
            />
            <SwitchIcon size="small" checked={onlyFollowed} />
          </Button>
          {!!so5Ranking?.so5Lineup && !!showCanBeatMe && (
            <Button
              color="tertiary"
              size="small"
              onClick={() => {
                const newValue = !canBeatMe;
                query({
                  variables: {
                    slug,
                    pageSize: PAGE_SIZE,
                    page: initialPage,
                    onlyFollowed,
                    canBeatSo5LineupId: newValue
                      ? idFromObject(so5Ranking.so5Lineup.id)
                      : undefined,
                  },
                });
                track('Toggle Can Beat Me', { canBeatMe: newValue });
                setCanBeatMe(prev => !prev);
              }}
            >
              <FormattedMessage
                id="leaderboard.filters.canBeatMe"
                defaultMessage="Can beat me"
              />
              <SwitchIcon size="small" checked={canBeatMe} />
            </Button>
          )}
        </Horizontal>
      </SBHorizontal>
      <Vertical>
        {loading ? (
          <LoadingIndicator fullHeight />
        ) : (
          so5Rankings?.map(ranking => (
            <RankRow
              key={ranking.id}
              ranking={ranking}
              isMe={ranking.id === so5Ranking?.id}
            />
          ))
        )}
      </Vertical>
      <Pagination
        currentPage={page || currentPage || 0}
        pages={pages || 0}
        onSelect={p => {
          setPage(p);
          query({
            variables: {
              slug,
              pageSize: PAGE_SIZE,
              page: p,
              onlyFollowed,
              canBeatSo5LineupId: canBeatMe
                ? idFromObject(so5Ranking?.so5Lineup.id)
                : undefined,
            },
          });
        }}
      />
    </Root>
  );
};

PaginatedRankings.fragments = {
  so5Ranking: gql`
    fragment PaginatedRankings_so5Ranking on So5Ranking {
      id
      ranking
      so5Lineup {
        id
      }
    }
  ` as TypedDocumentNode<PaginatedRankings_so5Ranking>,
};
